import clsx from "clsx";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetChat } from "~/api/chat/chat";
import { useGetCurrentUser } from "~/api/users/users";
import { DocAreaHeader } from "~/features/chat/components/docList/DocAreaHeader";
import { DocList } from "~/features/chat/components/docList/DocList";
import { DocSidebar } from "~/features/chat/components/docSidebar/DocSidebar";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { ROUTES, useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useSkyViewStore } from "../store";
import { DocViewer } from "./docViewer";
import { MobileBottomNav, MobileViewState } from "./MobileBottomNav";
import { PageSkeleton } from "./PageSkeleton";

export const SkyView = () => {
  const isDocSidebarExpanded = useSkyViewStore((state) => state.isExpanded);
  const isDocListCollapsed = useSkyViewStore(
    (state) => state.isDocListCollapsed
  );
  const toggleDocListCollapsed = useSkyViewStore(
    (state) => state.toggleDocListCollapsed
  );
  const navigate = useNavigate();
  const { organizationId, caseId, chatId, isRestricted, isGuest } =
    useChatDocumentParams();
  const { data: currentUser } = useGetCurrentUser();
  const { isMobile, isLoading: isMobileLoading } = useMobile();

  const [mobileView, setMobileView] = useState<MobileViewState>("docList");

  const { error: chatError } = useGetChat(organizationId, caseId, chatId, {
    query: {
      enabled: isGuest,
      retry: false,
    },
  });

  useEffect(() => {
    if (isGuest && chatError && (chatError as any).response?.status === 403) {
      navigate(ROUTES.ACCESS_DENIED);
    }
  }, [isGuest, chatError, navigate]);

  const handleNavigateBack = () => {
    navigate(ROUTES.CASE({ caseId, organizationId }));
  };

  const { pageItems, docList, isDocListLoading } = useDocList();

  const handleSectionSelect = useCallback(() => {
    if (isMobile) {
      setMobileView("viewer");
    }
  }, [isMobile]);

  if (!currentUser) return null;

  if (isMobileLoading) {
    return null;
  }

  if (isMobile) {
    return (
      <div className="flex flex-col h-screen-safe w-screen overflow-hidden">
        <div className="h-[60px] flex-shrink-0">
          <DocAreaHeader onNavigateBack={handleNavigateBack} />
        </div>
        <div className="flex-1 overflow-hidden h-skyview-content">
          {mobileView === "docList" && (
            <DocList onSectionSelect={handleSectionSelect} />
          )}
          {mobileView === "viewer" && (
            <>
              {isDocListLoading || !docList ? (
                <div className="flex flex-col items-center pt-3 px-3 gap-3 bg-muted h-full">
                  <PageSkeleton className="animate-pulse" />
                  <PageSkeleton className="animate-pulse" />
                </div>
              ) : (
                <DocViewer
                  pageItems={pageItems}
                  docList={docList}
                  isViewOnly={isRestricted}
                />
              )}
            </>
          )}
          {mobileView === "chat" && <DocSidebar isGuest={isGuest} />}
        </div>
        <MobileBottomNav activeView={mobileView} onViewChange={setMobileView} />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <div
        className={clsx("flex flex-1 min-w-0", {
          "w-full": !isDocSidebarExpanded,
          "w-3/4": isDocSidebarExpanded,
        })}
      >
        {!isDocListCollapsed && (
          <div className="flex h-full flex-col overflow-hidden w-1/2 flex-shrink-0">
            <div className="h-[60px] flex-shrink-0">
              <DocAreaHeader onNavigateBack={handleNavigateBack} />
            </div>
            <div className="flex-1 overflow-hidden">
              <DocList />
            </div>
          </div>
        )}
        {isDocListCollapsed ? (
          <div className="flex h-full items-center flex-shrink-0">
            <div
              className="flex h-12 w-6 cursor-pointer items-center justify-center rounded-r-full border border-l-0 border-border bg-muted shadow-sm transition-colors hover:bg-accent"
              onClick={toggleDocListCollapsed}
            >
              <ChevronsRight className="h-4 w-4" />
            </div>
          </div>
        ) : (
          <div
            className="w-3 h-full relative border-r border-l border-border flex items-center justify-center cursor-pointer hover:bg-accent flex-shrink-0"
            onClick={toggleDocListCollapsed}
          >
            <ChevronsLeft className="w-2 h-2" />
          </div>
        )}
        <div className="overflow-hidden flex-1" data-tour="doc-viewer">
          {isDocListLoading || !docList ? (
            <div className="flex flex-col items-center pt-3 px-3 gap-3 bg-muted h-full">
              <PageSkeleton className="animate-pulse" />
              <PageSkeleton className="animate-pulse" />
            </div>
          ) : (
            <DocViewer
              pageItems={pageItems}
              docList={docList}
              isViewOnly={isRestricted}
            />
          )}
        </div>
      </div>
      <div
        className={clsx("flex-shrink-0 overflow-hidden", {
          "w-1/2": isDocSidebarExpanded && isDocListCollapsed,
          "w-1/4": isDocSidebarExpanded && !isDocListCollapsed,
          "w-auto": !isDocSidebarExpanded,
        })}
      >
        <DocSidebar isGuest={isGuest} />
      </div>
    </div>
  );
};
